/* MyBoost motion — scroll-triggered animations and count-ups (ES module, no imports).
   Every [data-mbq-motion] container gets `is-in` while on screen (CSS does the rest) and its
   [data-mbq-count] numbers climb on each entry. Nothing moves under prefers-reduced-motion. */

export function easeOutCubic(p) {
  const t = Math.min(1, Math.max(0, Number(p) || 0));
  return 1 - (1 - t) ** 3;
}

export function formatCount(value, fmt = 'int', locale = 'fr') {
  const n = Number(value) || 0;
  let text;
  try {
    if (fmt === 'pct') text = `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(n)} %`;
    else if (fmt === 'dec') text = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(n);
    else text = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(n));
  } catch {
    if (fmt === 'pct') text = `${n.toFixed(1)} %`;
    else if (fmt === 'dec') text = n.toFixed(1);
    else text = String(Math.round(n));
  }
  return text.replace(/[  ]/g, ' ');
}

export function reducedMotion() {
  try {
    return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function countUp(el, { locale = 'fr', duration = 1400 } = {}) {
  const to = Number(el.dataset.mbqCount);
  const fmt = el.dataset.mbqFmt || 'int';
  if (!Number.isFinite(to)) return;
  if (el._mbqRaf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(el._mbqRaf);
  if (reducedMotion() || typeof requestAnimationFrame !== 'function') {
    el.textContent = formatCount(to, fmt, locale);
    return;
  }
  let start = null;
  const step = (now) => {
    if (start === null) start = now;
    const p = Math.min(1, (now - start) / duration);
    el.textContent = formatCount(to * easeOutCubic(p), fmt, locale);
    if (p < 1) el._mbqRaf = requestAnimationFrame(step);
    else el._mbqRaf = null;
  };
  el._mbqRaf = requestAnimationFrame(step);
}

// IntersectionObserver stays silent while a tab is hidden and before first paint (seen on mobile and in
// theme previews), so the first entry is also decided by a plain rectangle check.
const visible = (el) => {
  const r = el.getBoundingClientRect();
  return r.bottom > 0 && r.top < (globalThis.innerHeight || 0);
};

export function initMotion(root = document) {
  const locale = document.documentElement.lang || 'fr';
  const targets = [...root.querySelectorAll('[data-mbq-motion]')].filter((el) => el.dataset.mbqMotionReady !== 'true');
  if (!targets.length) return;
  const entered = new Set();
  let t1, t2;
  // Once every target has entered — or, when IntersectionObserver exists, once its own
  // callback has taken over after the last fallback sweep — the scroll/visibilitychange
  // listeners and pending sweeps have nothing left to do.
  const cleanup = () => {
    document.removeEventListener('visibilitychange', sweep);
    removeEventListener('scroll', sweep);
    clearTimeout(t1);
    clearTimeout(t2);
  };
  const enter = (el) => {
    if (!el.classList.contains('is-in')) {
      el.classList.add('is-in');
      el.querySelectorAll('[data-mbq-count]').forEach((n) => countUp(n, { locale }));
    }
    entered.add(el);
    if (entered.size === targets.length) cleanup();
  };
  const leave = (el) => el.classList.remove('is-in');
  targets.forEach((el) => { el.dataset.mbqMotionReady = 'true'; });
  const hasIO = typeof IntersectionObserver === 'function';
  if (hasIO) {
    const io = new IntersectionObserver((entries) => entries.forEach((e) => (e.isIntersecting ? enter(e.target) : leave(e.target))), { threshold: 0.2 });
    targets.forEach((el) => io.observe(el));
  }
  const sweep = () => targets.forEach((el) => { if (visible(el)) enter(el); });
  document.addEventListener('visibilitychange', sweep);
  addEventListener('scroll', sweep, { passive: true });
  t1 = setTimeout(sweep, 50);
  t2 = setTimeout(() => { sweep(); if (hasIO) cleanup(); }, 600);
  // Already-visible sections shouldn't wait for a fallback timer: sweep once synchronously
  // so their count-up starts immediately (this read also forces layout first, so the
  // `.is-in` transition still has a "before" state to animate from on the ones still off-screen).
  sweep();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initMotion());
  else initMotion();
  document.addEventListener('shopify:section:load', (event) => initMotion(event.target));
}
